import { useTranslation } from '../../../i18n';
import React, { useState, useEffect } from 'react';
import {
    Card,
    Table,
    Button,
    Space,
    Modal,
    Form,
    Input,
    message,
    Popconfirm,
    Switch,
    Tag,
    Alert
} from 'antd';
import {
    PlusOutlined,
    DeleteOutlined,
    SafetyOutlined,
    FileProtectOutlined
} from '@ant-design/icons';
import hostSharedService from '../../../../shared/services/hostSharedService';

const AntiUploadManager = ({ vhostName }) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [dataSource, setDataSource] = useState([]);
    const [modalVisible, setModalVisible] = useState(false);
    const [enabled, setEnabled] = useState(false);
    const [form] = Form.useForm();

    useEffect(() => {
        if (vhostName) {
            loadData();
        }
    }, [vhostName]);

    const loadData = async () => {
        setLoading(true);
        try {
            const response = await hostSharedService.getAntiUpload(vhostName);
            if (response.success) {
                const payload = response.data || {};
                setEnabled(!!payload.enabled);
                const itemsArray = Array.isArray(payload.rules)
                    ? payload.rules
                    : Array.isArray(payload.items)
                        ? payload.items
                        : Array.isArray(payload.data)
                            ? payload.data
                            : [];
                setDataSource(itemsArray.map((item, index) => ({
                    ...item,
                    id: item.id ?? index,
                    filename: item.filename ?? item.rule ?? item.name ?? '',
                    key: item.id ?? index,
                    enabled: item.enabled !== undefined ? item.enabled : true
                })));
            } else {
                message.error(response.error || t('Failed to load malicious upload protection rules'));
                setDataSource([]);
                setEnabled(false);
            }
        } catch (error) {
            message.error(t('Failed to load: {{message}}', { message: error.message }));
            setDataSource([]);
            setEnabled(false);
        } finally {
            setLoading(false);
        }
    };

    const handleAdd = () => {
        form.resetFields();
        setModalVisible(true);
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            const response = await hostSharedService.addAntiUpload(vhostName, values.filename);
            if (response.success) {
                message.success(t('Added successfully'));
                setModalVisible(false);
                loadData();
            } else {
                message.error(response.error || t('Failed to add'));
            }
        } catch (error) {
            if (error.errorFields) {
                // Form validation error
                return;
            }
            message.error(t('Failed to add: {{message}}', { message: error.message }));
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        setLoading(true);
        try {
            const response = await hostSharedService.deleteAntiUpload(vhostName, id);
            if (response.success) {
                message.success(t('Deleted successfully'));
                loadData();
            } else {
                message.error(response.error || t('Deletion failed'));
            }
        } catch (error) {
            message.error(t('Deletion failed: {{message}}', { message: error.message }));
        } finally {
            setLoading(false);
        }
    };

    const handleToggle = async (checked) => {
        setLoading(true);
        try {
            const response = await hostSharedService.toggleAntiUpload(vhostName, checked ? 1 : 0);
            if (response.success) {
                message.success(checked ? t('Malicious upload protection enabled') : t('Malicious upload protection disabled'));
                loadData();
            } else {
                message.error(response.error || t('Failed to change status'));
            }
        } catch (error) {
            message.error(t('Failed to change status: {{message}}', { message: error.message }));
        } finally {
            setLoading(false);
        }
    };

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 80,
        },
        {
            title: t('File name pattern'),
            dataIndex: 'filename',
            key: 'filename',
            render: (text) => <Tag color="orange">{text}</Tag>,
        },
        {
            title: t('Status'),
            dataIndex: 'enabled',
            key: 'enabled',
            width: 100,
            render: (enabled) => (
                <Tag color={enabled ? 'green' : 'default'}>
                    {enabled ? t('Enable') : t('Disable')}
                </Tag>
            ),
        },
        {
            title: t('Created at'),
            dataIndex: 'created_at',
            key: 'created_at',
            width: 180,
        },
        {
            title: t('Actions'),
            key: 'action',
            width: 120,
            render: (_, record) => (
                <Space>
                    <Popconfirm
                        title={t('Delete this rule?')}
                        onConfirm={() => handleDelete(record.id)}
                        okText={t('OK')}
                        cancelText={t('Cancel')}
                    >
                        <Button type="link" danger size="small" icon={<DeleteOutlined />}>
                            {t('Delete')}
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <Card
            title={
                <Space>
                    <FileProtectOutlined />
                    {t('Manage malware upload protection')}
                </Space>
            }
            extra={
                <Space>
                    <Switch
                        checkedChildren={t('Enable')}
                        unCheckedChildren={t('Disable')}
                        checked={enabled}
                        onChange={handleToggle}
                        loading={loading}
                    />
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={handleAdd}
                    >
                        {t('Add rule')}
                    </Button>
                </Space>
            }
        >
            <Alert
                message={t('About malware upload protection')}
                description={t('Add filename patterns to block uploads of specific file types. Wildcards are supported. Enter extensions without a leading dot, such as php, jsp, asp.')}
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
            />

            <Table
                columns={columns}
                dataSource={dataSource}
                loading={loading}
                pagination={{ pageSize: 10 }}
            />

            <Modal
                title={t('Add malicious upload protection rule')}
                open={modalVisible}
                onOk={handleSubmit}
                onCancel={() => setModalVisible(false)}
                confirmLoading={loading}
            >
                <Form form={form} layout="vertical">
                    <Form.Item
                        label={t('File name pattern')}
                        name="filename"
                        rules={[
                            { required: true, message: t('Please enter a filename pattern') },
                            { pattern: /^[\w\*\-]+$/, message: t('Only letters, numbers, *, -, and _ are allowed') }
                        ]}
                        extra={t('Supports the * wildcard, e.g., php, jsp, shell*')}
                    >
                        <Input placeholder={t('E.g. php')} />
                    </Form.Item>
                </Form>
            </Modal>
        </Card>
    );
};

export default AntiUploadManager;
